import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Calendar, CreditCard, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/Button';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';
import { stripeProducts, getProductByPriceId } from '../stripe-config';
import { useTranslation } from '../lib/i18n';

interface SubscriptionData {
  subscription_status: string;
  price_id: string | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
}

const SubscriptionStatus = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadSubscription();
      
      // Listen for subscription refresh events from Success page
      const handleRefreshSubscription = () => {
        console.log('🔄 SUBSCRIPTION STATUS: Received refresh event');
        loadSubscription();
      };
      
      window.addEventListener('refreshSubscription', handleRefreshSubscription);
      
      return () => {
        window.removeEventListener('refreshSubscription', handleRefreshSubscription);
      };
    }
  }, [user]);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 SubscriptionStatus: Loading subscription for user:', user?.id);
      console.log('🕐 SubscriptionStatus: Timestamp:', new Date().toISOString());
      console.log('🔄 SubscriptionStatus: Load triggered by component mount/refresh');

      // Get user's customer record
      console.log('👤 Getting customer record for user...');
      const { data: customerData, error: customerError } = await supabase
        .from('stripe_customers')
        .select('customer_id')
        .eq('user_id', user?.id)
        .maybeSingle();

      console.log('👤 SubscriptionStatus: Customer data:', customerData);
      console.log('❌ SubscriptionStatus: Customer error:', customerError);

      if (customerError || !customerData?.customer_id) {
        console.log('❌ No customer found');
        setSubscription(null);
        return;
      }

      // Get subscription for this customer
      console.log('📊 Getting subscription for customer:', customerData.customer_id);
      const { data: subData, error: subError } = await supabase
        .from('stripe_subscriptions')
        .select('*')
        .eq('customer_id', customerData.customer_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      console.log('📊 SubscriptionStatus: Subscription data:', subData);
      console.log('❌ SubscriptionStatus: Subscription error:', subError);

      if (subError || !subData) {
        console.log('❌ No subscription found');
        setSubscription(null);
        return;
      }

      // Map to expected format
      const mappedData = {
        customer_id: subData.customer_id,
        subscription_id: subData.subscription_id,
        subscription_status: subData.status,
        price_id: subData.price_id,
        current_period_start: subData.current_period_start,
        current_period_end: subData.current_period_end,
        cancel_at_period_end: subData.cancel_at_period_end,
        payment_method_brand: subData.payment_method_brand,
        payment_method_last4: subData.payment_method_last4
      };

      console.log('✅ SubscriptionStatus: Mapped subscription data:', mappedData);
      setSubscription(mappedData);
    } catch (err: any) {
      console.error('Error loading subscription:', err);
      console.error('Full error details:', JSON.stringify(err, null, 2));
      setError(err.message || t('subStatus.failedLoad'));
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeClick = () => {
    navigate('/pricing');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 dark:text-green-400';
      case 'trialing':
        return 'text-blue-600 dark:text-blue-400';
      case 'past_due':
      case 'unpaid':
        return 'text-red-600 dark:text-red-400';
      case 'canceled':
        return 'text-gray-600 dark:text-gray-400';
      default:
        return 'text-yellow-600 dark:text-yellow-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />;
      case 'trialing':
        return <Crown className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
      case 'past_due':
      case 'unpaid':
        return <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />;
      default:
        return <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />;
    }
  };

  const formatStatus = (status: string) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">{t('subStatus.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-lg p-6">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
          <span className="text-red-700 dark:text-red-200">{error}</span>
        </div>
      </div>
    );
  }

  if (!subscription || subscription.subscription_status === 'not_started') {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-6 border border-blue-200 dark:border-gray-600">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('subStatus.freePlan')}</h3>
            <p className="text-gray-600 dark:text-gray-400">{t('subStatus.upgradeDesc')}</p>
          </div>
          <Button 
            onClick={handleUpgradeClick}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            {t('subStatus.upgradeNow')}
          </Button>
        </div>
      </div>
    );
  }

  const product = subscription.price_id ? getProductByPriceId(subscription.price_id) : null;
  const periodEnd = subscription.current_period_end 
    ? new Date(subscription.current_period_end * 1000) 
    : null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Crown className="w-6 h-6 text-purple-600 dark:text-purple-400 mr-3" />
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {product?.name || t('subStatus.premiumPlan')}
            </h3>
            <div className="flex items-center">
              {getStatusIcon(subscription.subscription_status)}
              <span className={`ml-2 text-sm font-medium ${getStatusColor(subscription.subscription_status)}`}>
                {formatStatus(subscription.subscription_status)}
              </span>
            </div>
          </div>
        </div>
        {product && (
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              ${product.price}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {product.mode === 'subscription' ? t('subStatus.perMonth') : t('subStatus.oneTime')}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {periodEnd && (
          <div className="flex items-center">
            <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {subscription.cancel_at_period_end ? t('subStatus.expires') : t('subStatus.renews')} on
              </div>
              <div className="font-medium">
                {periodEnd.toLocaleDateString()}
              </div>
            </div>
          </div>
        )}

        {subscription.payment_method_brand && subscription.payment_method_last4 && (
          <div className="flex items-center">
            <CreditCard className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-2" />
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('subStatus.paymentMethod')}</div>
              <div className="font-medium">
                {subscription.payment_method_brand.toUpperCase()} •••• {subscription.payment_method_last4}
              </div>
            </div>
          </div>
        )}
      </div>

      {subscription.cancel_at_period_end && (
        <div className="mt-4 p-3 bg-yellow-100 dark:bg-yellow-900/50 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mr-2" />
            <span className="text-sm text-yellow-700 dark:text-yellow-200">
              {t('subStatus.willExpire', { date: periodEnd?.toLocaleDateString() })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionStatus;